import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { customerAPI } from '../services/api';
import { ArrowLeft, Phone, Download, Loader, Tv, Wifi } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// Monthly Google Contacts sync — one CSV per service, matching the LCV_<mon><yy>.csv /
// AIS_<mon><yy>.csv files the owner used to build by hand every month. Same download logic
// as the "CableTV Contacts" / "Broadband Contacts" buttons on the Customers page toolbar —
// this page just gives the feature its own place under Admin Settings > Customer Updates.
export default function ExportContactsPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(null); // 'catv' | 'broadband' | null

  const download = async (service) => {
    setDownloading(service);
    try {
      const { data } = await customerAPI.exportContacts({ service });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${service === 'catv' ? 'LCV' : 'AIS'}_${format(new Date(), 'MMMyy').toUpperCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Contacts CSV downloaded — import it at contacts.google.com');
    } catch (err) {
      toast.error('Contacts export failed');
    } finally {
      setDownloading(null);
    }
  };

  const Card = ({ service, icon: Icon, title, subtitle }) => (
    <div className="card mb-4">
      <div className="card-header">
        <h3 style={{ margin: 0 }}>
          <Icon size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />
          {title}
        </h3>
      </div>
      <div className="card-body">
        <p className="text-muted text-small" style={{ marginTop: 0 }}>{subtitle}</p>
        <button className="btn btn-primary" disabled={!!downloading} onClick={() => download(service)}>
          {downloading === service ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
          {downloading === service ? 'Preparing…' : `Download ${service === 'catv' ? 'LCV' : 'AIS'}_${format(new Date(), 'MMMyy').toUpperCase()}.csv`}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} />
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
            <Phone size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Export Contacts
          </h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            Google Contacts CSV, ready to import at contacts.google.com — active customers only.
          </p>
        </div>
      </div>

      <Card
        service="catv" icon={Tv} title="CableTV Contacts"
        subtitle="Given Name = address, Nickname = customer name, Birthday = CATV expiry, Group = society name."
      />
      <Card
        service="broadband" icon={Wifi} title="Broadband Contacts"
        subtitle="Given Name = address + ISP, Nickname = customer name, Birthday = broadband expiry, Group = AIS (ISP)."
      />
    </div>
  );
}
